import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { AuthContext, API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Repeat, Pill, Clock, Trash2, CalendarDays, ArrowUpCircle, MinusCircle, ArrowDownCircle } from 'lucide-react';

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const FREQ_LABEL = { daily: 'Every day', weekly: 'Weekly', custom: 'Custom' };

import AppLogo from '../components/AppLogo';

const RecurringTasksPage = () => {
  const { user } = useContext(AuthContext);
  const [circles, setCircles] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [filter, setFilter] = useState('all');
  const [newTask, setNewTask] = useState({
    circle_id: '', title: '', description: '', frequency: 'daily',
    days_of_week: [], time_of_day: '', category: 'task', priority: 'medium'
  });

  const fetchData = useCallback(async () => {
    try {
      const [circlesRes, tasksRes] = await Promise.all([
        axios.get(`${API}/circles`),
        axios.get(`${API}/my-recurring-tasks`)
      ]);
      setCircles(circlesRes.data);
      setTasks(tasksRes.data);
      if (circlesRes.data.length > 0) {
        setNewTask(prev => prev.circle_id ? prev : { ...prev, circle_id: circlesRes.data[0].id });
      }
    } catch (e) { toast.error('Could not load routines'); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newTask.circle_id || !newTask.title || !newTask.frequency) {
      toast.error('Please fill in all required fields'); return;
    }
    try {
      const payload = { ...newTask };
      if (payload.frequency !== 'custom' && payload.frequency !== 'weekly') {
        payload.days_of_week = null;
      }
      await axios.post(`${API}/recurring-tasks`, payload);
      toast.success(newTask.category === 'medication' ? 'Medication reminder set!' : 'Recurring task created!');
      setCreateDialogOpen(false);
      setNewTask({ circle_id: circles[0]?.id || '', title: '', description: '', frequency: 'daily', days_of_week: [], time_of_day: '', category: 'task', priority: 'medium' });
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Could not create'); }
  };

  const handleDelete = async () => {
    if (!taskToDelete) return;
    try {
      await axios.delete(`${API}/recurring-tasks/${taskToDelete}`);
      toast.success('Removed');
      setDeleteConfirmOpen(false);
      setTaskToDelete(null);
      fetchData();
    } catch (error) { toast.error('Could not remove'); }
  };

  const toggleDay = (day) => {
    const days = newTask.days_of_week || [];
    setNewTask({
      ...newTask,
      days_of_week: days.includes(day) ? days.filter(d => d !== day) : [...days, day]
    });
  };

  const filteredTasks = tasks.filter(t => {
    if (filter === 'medication') return t.category === 'medication';
    if (filter === 'task') return t.category === 'task';
    return true;
  });

  const PRIORITY_CONFIG = {
    high: { icon: ArrowUpCircle, color: 'text-rose-500', bg: 'bg-rose-50 text-rose-700' },
    medium: { icon: MinusCircle, color: 'text-amber-500', bg: 'bg-amber-50 text-amber-700' },
    low: { icon: ArrowDownCircle, color: 'text-sky-500', bg: 'bg-sky-50 text-sky-700' },
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-10 w-10 border-2 border-cyan-200 border-t-cyan-600"></div></div>;
  }

  return (
    <div className="min-h-screen pb-24" data-testid="recurring-tasks-page">
      <header className="surface-header sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-5 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AppLogo />
            <div>
              <h1 className="text-xl font-bold text-foreground tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Routines & Meds</h1>
              <p className="text-xs text-muted-foreground">{filteredTasks.length} active routine{filteredTasks.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary h-9 text-sm" data-testid="add-recurring-btn">
                <Plus className="w-4 h-4 mr-1.5" /> Add
              </Button>
            </DialogTrigger>
            <DialogContent className="surface-card border-0 shadow-xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>New Routine</DialogTitle>
                <DialogDescription>Set up a recurring task or medication reminder.</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div className="flex gap-2">
                  <button type="button" onClick={() => setNewTask({ ...newTask, category: 'task' })}
                    className={`flex-1 py-2.5 px-3 rounded-xl text-sm font-semibold transition-all ${newTask.category === 'task' ? 'bg-cyan-600 text-white' : 'bg-muted text-muted-foreground'}`}
                    data-testid="cat-task">
                    <Repeat className="w-4 h-4 inline mr-1.5" />Task
                  </button>
                  <button type="button" onClick={() => setNewTask({ ...newTask, category: 'medication' })}
                    className={`flex-1 py-2.5 px-3 rounded-xl text-sm font-semibold transition-all ${newTask.category === 'medication' ? 'bg-rose-500 text-white' : 'bg-muted text-muted-foreground'}`}
                    data-testid="cat-medication">
                    <Pill className="w-4 h-4 inline mr-1.5" />Medication
                  </button>
                </div>
                <div className="space-y-2">
                  <Label>Circle</Label>
                  <Select value={newTask.circle_id} onValueChange={(val) => setNewTask({ ...newTask, circle_id: val })}>
                    <SelectTrigger className="rounded-xl" data-testid="rt-circle-select"><SelectValue placeholder="Select circle" /></SelectTrigger>
                    <SelectContent>{circles.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{newTask.category === 'medication' ? 'Medication name & dosage' : 'Task name'}</Label>
                  <Input data-testid="rt-title-input" placeholder={newTask.category === 'medication' ? 'e.g., Ibuprofen 400mg' : 'e.g., Check blood pressure'} value={newTask.title} onChange={(e) => setNewTask({ ...newTask, title: e.target.value })} className="rounded-xl" required />
                </div>
                <div className="space-y-2">
                  <Label>Notes (optional)</Label>
                  <Textarea data-testid="rt-desc-input" placeholder="Additional instructions..." value={newTask.description} onChange={(e) => setNewTask({ ...newTask, description: e.target.value })} className="resize-none rounded-xl" rows={2} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Frequency</Label>
                    <Select value={newTask.frequency} onValueChange={(val) => setNewTask({ ...newTask, frequency: val })}>
                      <SelectTrigger className="rounded-xl" data-testid="rt-freq-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Every day</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="custom">Custom days</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Time</Label>
                    <Input type="time" data-testid="rt-time-input" value={newTask.time_of_day} onChange={(e) => setNewTask({ ...newTask, time_of_day: e.target.value })} className="rounded-xl" />
                  </div>
                </div>
                {(newTask.frequency === 'weekly' || newTask.frequency === 'custom') && (
                  <div className="space-y-2">
                    <Label>Days</Label>
                    <div className="flex gap-1.5">
                      {DAY_NAMES.map((name, idx) => (
                        <button key={name} type="button" onClick={() => toggleDay(idx)}
                          className={`w-10 h-10 rounded-xl text-xs font-bold transition-all ${(newTask.days_of_week || []).includes(idx) ? 'bg-cyan-600 text-white' : 'bg-muted text-muted-foreground hover:bg-slate-200'}`}
                          data-testid={`day-${idx}`}>
                          {name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select value={newTask.priority} onValueChange={(val) => setNewTask({ ...newTask, priority: val })}>
                    <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="high"><span className="flex items-center gap-2"><ArrowUpCircle className="w-3.5 h-3.5 text-rose-500" /> High</span></SelectItem>
                      <SelectItem value="medium"><span className="flex items-center gap-2"><MinusCircle className="w-3.5 h-3.5 text-amber-500" /> Medium</span></SelectItem>
                      <SelectItem value="low"><span className="flex items-center gap-2"><ArrowDownCircle className="w-3.5 h-3.5 text-sky-500" /> Low</span></SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="w-full btn-primary" data-testid="submit-recurring-btn">
                  {newTask.category === 'medication' ? 'Set Medication Reminder' : 'Create Recurring Task'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-4 space-y-5">
        {/* Filters */}
        <div className="flex gap-2">
          {[
            { value: 'all', label: 'All' },
            { value: 'medication', label: 'Medications' },
            { value: 'task', label: 'Tasks' }
          ].map(f => (
            <button key={f.value} onClick={() => setFilter(f.value)}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all ${filter === f.value ? 'bg-cyan-600 text-white' : 'bg-card text-muted-foreground border border-border/50 hover:bg-muted'}`}
              data-testid={`filter-${f.value}`}>
              {f.label}
            </button>
          ))}
        </div>

        {/* Task List */}
        {filteredTasks.length > 0 ? (
          <div className="space-y-2">
            {filteredTasks.map(task => {
              const p = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.medium;
              const PIcon = p.icon;
              const isMed = task.category === 'medication';
              return (
                <div key={task.id} className="surface-card p-4" data-testid={`recurring-${task.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isMed ? 'bg-rose-50' : 'bg-cyan-50'}`}>
                        {isMed ? <Pill className="w-5 h-5 text-rose-500" /> : <Repeat className="w-5 h-5 text-cyan-600" />}
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground">{task.title}</h4>
                        {task.description && <p className="text-xs text-muted-foreground mt-0.5">{task.description}</p>}
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${p.bg}`}>
                            <PIcon className="w-3 h-3 inline mr-0.5" />{task.priority}
                          </span>
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-slate-600 font-medium flex items-center gap-1">
                            <CalendarDays className="w-3 h-3" />
                            {FREQ_LABEL[task.frequency] || 'Custom'}
                          </span>
                          {task.time_of_day && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-slate-600 font-medium flex items-center gap-1">
                              <Clock className="w-3 h-3" />{task.time_of_day}
                            </span>
                          )}
                          {task.days_of_week && task.days_of_week.length > 0 && (
                            <span className="text-[10px] text-muted-foreground">
                              {task.days_of_week.map(d => DAY_NAMES[d]).join(', ')}
                            </span>
                          )}
                          {task.circle_name && <span className="text-[10px] text-cyan-600 font-medium">{task.circle_name}</span>}
                        </div>
                      </div>
                    </div>
                    {task.created_by === user?.id && (
                      <button onClick={() => { setTaskToDelete(task.id); setDeleteConfirmOpen(true); }}
                        className="p-2 rounded-lg hover:bg-rose-50 transition-colors flex-shrink-0"
                        data-testid={`delete-recurring-${task.id}`}>
                        <Trash2 className="w-4 h-4 text-rose-400" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-20 surface-card">
            <Repeat className="w-14 h-14 text-cyan-200 mx-auto mb-4" />
            <p className="text-base font-semibold text-foreground mb-1">
              {filter === 'all' ? 'No routines yet' : `No ${filter === 'medication' ? 'medications' : 'recurring tasks'}`}
            </p>
            <p className="text-xs text-muted-foreground/60 mb-5 max-w-xs mx-auto">
              Set up medication reminders or recurring tasks so nothing falls through the cracks.
            </p>
            <Button onClick={() => setCreateDialogOpen(true)} className="btn-primary" data-testid="empty-add-recurring-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Add Routine
            </Button>
          </div>
        )}
      </main>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this routine?</AlertDialogTitle>
            <AlertDialogDescription>This will stop the recurring schedule. Existing tasks won't be affected.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-delete-recurring">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default RecurringTasksPage;
